/**
 * Glyphs as a texture atlas — real letterforms, not a point-cloud stipple.
 *
 * This replaced a blue-noise dot-scatter per character. That was legible in
 * principle but not in practice: the device capture of 2026-08-25 showed text
 * reading as "crumbs" rather than words, because a sparse scatter of a few dozen
 * points per glyph only resolves at a narrow band of apparent size — too far
 * and the dots merge into noise, too close and the gaps between them show. A
 * solid, antialiased letterform degrades gracefully across the same range: it
 * is still a recognisable "e" whether it is crisp or slightly soft.
 *
 * Glyphs are still sampled at runtime rather than shipped, for the same reason
 * as before — the corpus can contain any character (Latin, Cyrillic, whatever a
 * fragment mutates into) without a new asset. Each character is drawn once into
 * a fixed-size cell of a shared canvas atlas; `layoutText` in textCloud.ts then
 * builds textured quads that reference cells by UV rect, so the GPU cost is one
 * texture and a handful of triangles per sentence, not thousands of points.
 *
 * ── Why every glyph shares one quad shape ───────────────────────────────────
 * The atlas is a fixed grid, so every cell has the same size and the same
 * origin/baseline placement within it. That means the em-local quad corners
 * (GLYPH_EM_LEFT/RIGHT/TOP/BOTTOM) are identical for every character — only the
 * UV rect differs. Simpler than per-glyph bounding boxes, and the wasted margin
 * around a narrow glyph like "i" costs nothing: the cell's empty pixels are
 * fully transparent and contribute nothing to the render.
 */

import { BoundedCache, REPLACEMENT_CHAR, SlotAllocator } from './glyphSlots.js';

/** Font size glyphs are rasterised at, in canvas px. */
const RASTER = 34;
/** Atlas cell size, in canvas px. Bigger than RASTER to leave room for
 *  ascenders, descenders and diacritics without clipping. */
const CELL = 48;
/** Left bearing within a cell, in canvas px. */
const ORIGIN_X = 6;
/** Baseline position within a cell, in canvas px, measured from the top. */
const BASELINE_Y = 36;
/** Texel inset on each UV edge, to keep bilinear filtering from bleeding the
 *  neighbouring cell in at a glyph's border. */
const UV_INSET = 0.5;

const ATLAS_SIZE = 1024;
const COLS = Math.floor(ATLAS_SIZE / CELL);
const MAX_GLYPHS = COLS * COLS;
/** Ceiling on cached entries (slotted glyphs plus whitespace / no-ink ones). */
const CACHE_MAX = MAX_GLYPHS * 2;

/** The quad every glyph shares, in ems relative to (pen x, baseline y). */
export const GLYPH_EM_LEFT = -ORIGIN_X / RASTER;
export const GLYPH_EM_RIGHT = (CELL - ORIGIN_X) / RASTER;
export const GLYPH_EM_TOP = BASELINE_Y / RASTER;
export const GLYPH_EM_BOTTOM = (BASELINE_Y - CELL) / RASTER;

export interface Glyph {
  char: string;
  /** Horizontal advance in ems. */
  advance: number;
  /** False for whitespace and anything the font renders as nothing — no quad
   *  should be emitted for these. */
  hasGlyph: boolean;
  /** Atlas UV rect, already inset. Meaningless when hasGlyph is false. */
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

const cache = new BoundedCache<Glyph>(CACHE_MAX);
/** Cell allocator. The last cell is reserved for the replacement glyph, and a
 *  cell once handed out is never rewritten — see glyphSlots.ts. */
const slots = new SlotAllocator(MAX_GLYPHS);
/** The replacement glyph, drawn into its reserved cell on first overflow. */
let replacementGlyph: Glyph | null = null;
let atlasCanvas: HTMLCanvasElement | null = null;
let atlasCtx: CanvasRenderingContext2D | null = null;
let version = 0;
let fontSpec = `${RASTER}px "Times New Roman", Georgia, serif`;

/**
 * Set the face glyphs are sampled from — a full CSS font string including the
 * `${RASTER}px` size, e.g. `` `${RASTER}px Georgia` ``. Must be called before
 * any sampling to take effect; already-cached glyphs are dropped and the atlas
 * restarts empty.
 */
export function setGlyphFont(cssFont: string): void {
  fontSpec = cssFont;
  cache.clear();
  slots.reset();
  replacementGlyph = null;
  version++;
  if (atlasCtx) atlasCtx.clearRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);
}

function atlas(): CanvasRenderingContext2D | null {
  if (atlasCtx) return atlasCtx;
  const c = document.createElement('canvas');
  c.width = ATLAS_SIZE;
  c.height = ATLAS_SIZE;
  atlasCanvas = c;
  atlasCtx = c.getContext('2d');
  return atlasCtx;
}

/** The atlas as a drawable image source, for uploading to a GL texture. */
export function atlasImage(): CanvasImageSource | null {
  atlas(); // ensure created
  return atlasCanvas;
}

/** Bumped whenever a new glyph is rasterised. Callers use this to know when
 *  their uploaded copy of the atlas texture is stale. */
export function atlasVersion(): number {
  return version;
}

/** Sample one character. Cached; repeated calls are free. */
export function glyph(char: string): Glyph {
  const hit = cache.get(char);
  if (hit) return hit;

  const c = atlas();
  if (!c) {
    const g: Glyph = { char, advance: 0.5, hasGlyph: false, u0: 0, v0: 0, u1: 0, v1: 0 };
    cache.set(char, g);
    return g;
  }

  c.font = fontSpec;
  c.textBaseline = 'alphabetic';
  const advance = c.measureText(char).width / RASTER;

  if (!char.trim() || advance === 0) {
    const g: Glyph = { char, advance: advance || 0.3, hasGlyph: false, u0: 0, v0: 0, u1: 0, v1: 0 };
    cache.set(char, g);
    return g;
  }

  const slot = slots.alloc();
  if (slot === null) {
    // The corpus asked for more distinct characters than the atlas has room for
    // (vanishingly unlikely for real text). The previous behaviour rewrote the
    // LAST cell for every further character, corrupting every glyph already
    // cached at that UV. Now no occupied cell is ever touched: the character is
    // drawn as the replacement glyph, with its own advance so the line still
    // lays out. Not cached — arbitrary input must not grow the cache.
    const rep = replacement(c);
    return { ...rep, char, advance };
  }

  const g = drawCell(c, slot, char, advance);
  cache.set(char, g);
  return g;
}

/** Rasterise `char` into atlas cell `slot` and describe it. Only ever called
 *  on a cell that is empty (freshly allocated, or the reserved replacement). */
function drawCell(c: CanvasRenderingContext2D, slot: number, char: string, advance: number): Glyph {
  const col = slot % COLS;
  const row = (slot / COLS) | 0;
  const cellX = col * CELL;
  const cellY = row * CELL;

  c.clearRect(cellX, cellY, CELL, CELL);
  c.fillStyle = '#fff';
  c.fillText(char, cellX + ORIGIN_X, cellY + BASELINE_Y);
  version++;

  return {
    char,
    advance,
    hasGlyph: true,
    u0: (cellX + UV_INSET) / ATLAS_SIZE,
    v0: 1 - (cellY + UV_INSET) / ATLAS_SIZE,
    u1: (cellX + CELL - UV_INSET) / ATLAS_SIZE,
    v1: 1 - (cellY + CELL - UV_INSET) / ATLAS_SIZE,
  };
}

/** The reserved-cell replacement glyph, drawn on first use. */
function replacement(c: CanvasRenderingContext2D): Glyph {
  if (!replacementGlyph) {
    c.font = fontSpec;
    c.textBaseline = 'alphabetic';
    const adv = c.measureText(REPLACEMENT_CHAR).width / RASTER;
    replacementGlyph = drawCell(c, slots.replacementSlot, REPLACEMENT_CHAR, adv || 0.6);
  }
  return replacementGlyph;
}

/** Pre-sample a set of characters so the first sentence does not stutter. */
export function warmGlyphs(chars: string): void {
  for (const ch of chars) glyph(ch);
}

export function __clearGlyphCache(): void {
  cache.clear();
  slots.reset();
  replacementGlyph = null;
  version = 0;
  atlasCtx?.clearRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);
}
